import React, { useEffect, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import './actionsMenu.css';

function stopGridEvent(event, { preventDefault = false } = {}) {
  if (!event) return;
  if (preventDefault && typeof event.preventDefault === 'function') {
    event.preventDefault();
  }
  if (typeof event.stopPropagation === 'function') {
    event.stopPropagation();
  }
  const native = event.nativeEvent || event;
  if (native && typeof native.stopImmediatePropagation === 'function') {
    native.stopImmediatePropagation();
  }
}

function resolveButtonElement(event) {
  if (!event) return null;
  if (event.currentTarget && typeof event.currentTarget.getBoundingClientRect === 'function') {
    return event.currentTarget;
  }
  const target = event.target;
  if (target && typeof target.closest === 'function') {
    return target.closest('button') || target.closest('.ilocal-ag-actions-cell') || target;
  }
  return target || null;
}

/**
 * Build a stable viewport position from the clicked button (preferred) or mouse event.
 */
export function createMenuPositionEvent(eventOrElement) {
  let clientX = 0;
  let clientY = 0;

  const el =
    eventOrElement && typeof eventOrElement.getBoundingClientRect === 'function'
      ? eventOrElement
      : null;

  if (el) {
    const rect = el.getBoundingClientRect();
    clientX = Math.round(rect.left);
    clientY = Math.round(rect.bottom + 4);
  } else if (eventOrElement) {
    const native = eventOrElement.nativeEvent || eventOrElement;
    if (typeof native.clientX === 'number' && typeof native.clientY === 'number') {
      clientX = Math.round(native.clientX);
      clientY = Math.round(native.clientY);
    }
  }

  return {
    clientX,
    clientY,
    pageX: clientX + (typeof window !== 'undefined' ? window.pageXOffset || 0 : 0),
    pageY: clientY + (typeof window !== 'undefined' ? window.pageYOffset || 0 : 0),
    preventDefault() {},
    stopPropagation() {},
    target: el || null,
    currentTarget: el || null,
  };
}

const ACTION_ICON_SVGS = {
  view:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>',
  edit:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>',
  clone:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>',
  delete:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>',
  disable:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8 0-1.85.63-3.55 1.69-4.9L16.9 18.31C15.55 19.37 13.85 20 12 20zm6.31-3.1L7.1 5.69C8.45 4.63 10.15 4 12 4c4.42 0 8 3.58 8 8 0 1.85-.63 3.55-1.69 4.9z"/></svg>',
  enable:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>',
  password:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12.65 10C11.83 7.67 9.61 6 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6c2.61 0 4.83-1.67 5.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/></svg>',
  unlock:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 17c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm6-9h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6h1.9c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm0 12H6V10h12v10z"/></svg>',
  key:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12.65 10C11.83 7.67 9.61 6 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6c2.61 0 4.83-1.67 5.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/></svg>',
  print:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"/></svg>',
  download:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 20h14v-2H5v2zM19 9h-4V3H9v6H5l7 7 7-7z"/></svg>',
  email:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>',
  users:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>',
  mapping:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM15 19l-6-2.11V5l6 2.11V19z"/></svg>',
  history:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/></svg>',
};

function iconHtml(iconKeyOrHtml) {
  if (!iconKeyOrHtml) return undefined;
  const svg = ACTION_ICON_SVGS[iconKeyOrHtml] || iconKeyOrHtml;
  return `<span class="ilocal-ag-action-icon">${svg}</span>`;
}

export function actionMenuItem({ name, action, icon, disabled = false, cssClasses }) {
  return {
    name,
    action,
    disabled: !!disabled,
    iconKey: ACTION_ICON_SVGS[icon] ? icon : null,
    icon: iconHtml(icon),
    cssClasses: ['ilocal-ag-action-item'].concat(cssClasses || []),
  };
}

/** Grid-level menu host — survives AG Grid cell remounts after parent setState. */
let actionsMenuListener = null;

export function showRowActionsMenu({ items, position }) {
  if (typeof actionsMenuListener === 'function') {
    actionsMenuListener({
      open: true,
      items: Array.isArray(items) ? items : [],
      position: position || null,
    });
    return true;
  }
  return false;
}

export function hideRowActionsMenu() {
  if (typeof actionsMenuListener === 'function') {
    actionsMenuListener({ open: false, items: [], position: null });
  }
}

/**
 * Fallback: open AG Grid context menu (right-click). Prefer MUI host for ⋮.
 */
export function openAgGridContextMenu(params, positionEvent) {
  if (!params || !params.api) {
    return false;
  }
  const factory = params.api.contextMenuFactory;
  if (!factory || typeof factory.showMenu !== 'function') {
    return false;
  }

  const mouseEvent =
    positionEvent && typeof positionEvent.clientX === 'number'
      ? positionEvent
      : createMenuPositionEvent(positionEvent);

  return factory.showMenu(
    params.node,
    params.column,
    params.value,
    mouseEvent
  );
}

function ActionIcon({ iconKey, html }) {
  if (iconKey && ACTION_ICON_SVGS[iconKey]) {
    return (
      <span
        className="ilocal-ag-action-icon"
        dangerouslySetInnerHTML={{ __html: ACTION_ICON_SVGS[iconKey] }}
      />
    );
  }
  if (html) {
    return (
      <span
        className="ilocal-ag-action-icon"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }
  return null;
}

/** Mount once per grid (in ILocalAGGrid). Portals the MUI Menu to document.body. */
export function RowActionsMenuHost() {
  const [menuState, setMenuState] = useState({
    open: false,
    items: [],
    position: null,
  });

  useEffect(() => {
    actionsMenuListener = setMenuState;
    return () => {
      if (actionsMenuListener === setMenuState) {
        actionsMenuListener = null;
      }
    };
  }, []);

  const closeMenu = () => {
    setMenuState({ open: false, items: [], position: null });
  };

  return (
    <Menu
      open={Boolean(menuState.open && menuState.position)}
      onClose={closeMenu}
      anchorReference="anchorPosition"
      anchorPosition={menuState.position || undefined}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      disableScrollLock
      transitionDuration={0}
      MenuListProps={{
        dense: true,
        autoFocusItem: false,
        className: 'ilocal-ag-actions-menu-list',
      }}
      PaperProps={{ className: 'ilocal-ag-actions-menu-paper' }}
    >
      {(menuState.items || []).map((item, index) => {
        if (!item || item === 'separator') {
          return <Divider key={`sep-${index}`} />;
        }
        return (
          <MenuItem
            key={`${item.name}-${index}`}
            disabled={!!item.disabled}
            className="ilocal-ag-actions-menu-item"
            onClick={() => {
              closeMenu();
              if (typeof item.action === 'function') {
                item.action();
              }
            }}
          >
            <ListItemIcon className="ilocal-ag-actions-menu-item-icon">
              <ActionIcon iconKey={item.iconKey} html={item.icon} />
            </ListItemIcon>
            <ListItemText primary={item.name} />
          </MenuItem>
        );
      })}
    </Menu>
  );
}

export function ActionsMenuCellRenderer(params) {
  const onActionsClick = params && params.onActionsClick;

  return (
    <div
      className="ilocal-ag-actions-cell"
      onMouseDown={(e) => stopGridEvent(e)}
      onMouseUp={(e) => stopGridEvent(e)}
      onClick={(e) => stopGridEvent(e)}
      style={{ display: 'inline-flex', alignItems: 'center' }}
    >
      <IconButton
        aria-label="More"
        aria-haspopup="true"
        size="small"
        onMouseDown={(e) => stopGridEvent(e)}
        onClick={(event) => {
          const buttonEl = resolveButtonElement(event);
          const positionEvent = createMenuPositionEvent(buttonEl || event);
          const position = {
            top: positionEvent.clientY,
            left: positionEvent.clientX,
          };
          stopGridEvent(event);

          const helpers = {
            openMenu: (items) => showRowActionsMenu({ items, position }),
            positionEvent,
            position,
          };

          if (typeof onActionsClick === 'function') {
            onActionsClick(event, params, helpers);
          }
        }}
      >
        <MoreVertIcon style={{ fontSize: 20 }} />
      </IconButton>
    </div>
  );
}

export function getActionsColumn({
  headerName = 'Actions',
  field = 'actions',
  minWidth = 100,
  maxWidth = 120,
  onActionsClick,
  pinned,
} = {}) {
  return {
    field,
    colId: field,
    headerName,
    minWidth,
    maxWidth,
    flex: 0,
    filter: false,
    sortable: false,
    suppressMenu: true,
    suppressNavigable: true,
    cellClass: 'ilocal-ag-actions-col',
    cellRenderer: ActionsMenuCellRenderer,
    cellRendererParams: onActionsClick
      ? { onActionsClick }
      : undefined,
    ...(pinned ? { pinned, lockPinned: true } : {}),
  };
}

export function withClickableCell(colDef, onClick) {
  if (!colDef) return colDef;
  const existingClass = colDef.cellClass;
  const cellClass = [existingClass, 'ilocal-ag-clickable-cell']
    .flat()
    .filter(Boolean)
    .join(' ');
  return {
    ...colDef,
    cellClass,
    cellRenderer: (params) => {
      const value =
        params.valueFormatted != null && params.valueFormatted !== ''
          ? params.valueFormatted
          : params.value;
      if (value == null || value === '') return '';
      return (
        <a
          href="#view"
          className="ilocal-ag-clickable-link"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (typeof onClick === 'function' && params.data) {
              onClick(event, params.data);
            }
          }}
        >
          {String(value)}
        </a>
      );
    },
  };
}

export function getReportViewActionsColumn({ t, onView, headerName } = {}) {
  const actionsLabel =
    headerName ||
    (typeof t === 'function' && t('Location.Actions', { defaultValue: 'Actions' })) ||
    'Actions';
  const viewLabel =
    (typeof t === 'function' && t('Location.ViewActions', { defaultValue: 'View' })) ||
    'View';
  return getActionsColumn({
    headerName: actionsLabel,
    pinned: 'right',
    onActionsClick: (event, params, menuHelpers) => {
      const row = params && params.data;
      if (!row || typeof onView !== 'function') return;
      if (menuHelpers && typeof menuHelpers.openMenu === 'function') {
        menuHelpers.openMenu([
          actionMenuItem({
            name: viewLabel,
            icon: 'view',
            action: () => onView(row),
          }),
        ]);
      }
    },
  });
}

export function resolveGridRow(rowOrId, rows, matchFn) {
  if (rowOrId && typeof rowOrId === 'object') return rowOrId;
  if (typeof matchFn !== 'function') return null;
  return (rows || []).find((row) => matchFn(row, rowOrId)) || null;
}

export function isActionsColumn(column) {
  if (!column) return false;
  const colId =
    typeof column.getColId === 'function' ? column.getColId() : column.colId;
  return colId === 'actions';
}



// WEBPACK FOOTER //
// ./src/components/grid/actionsMenu.js